import axios from "axios";
import API from "../api";
import { AuthService } from "../services/auth.service";
import AuthUtils from "../utils/auth.utils";
import qs from "querystring";

export const AuthModule = {
  state: {
    isRequesting: false,
    isRequestingUserData: false,
    isAuthenticated: false,
    isUpdatingUser: false,
    errored: false,
    error: "",
    passwordResetEmail: "",
    resetToken: null,
    resetTokenValid: false,
    user: {
      id: "",
      email: "",
      first_name: "",
      last_name: "",
      middle_name: "",
      birthday: "",
      sex: "",
      address: "",
      phone: "",
      country_of_origin: "",
      citizenship: "",
      passport_number: "",
      photo_uri: "",
      snapchat: "",
      pinterest: "",
      twitter: "",
      instagram: "",
      facebook: "",
    },
  },
  actions: {
    async apiLogin({ commit, dispatch }, credentials) {
      commit("loginRequest");

      try {
        const response = await AuthService.login(credentials);
        const { access_token, user } = response.data;

        AuthUtils.setToken(access_token);
        commit("loginSuccess");
        commit("userDataSuccess", user);
        await dispatch("subscription/fetchSubscriptionData");

        return response.data;
      } catch (error) {
        commit("loginError", error.message);
        throw error;
      }
    },

    async apiRegister({ commit, dispatch }, userData) {
      commit("registerRequest");

      try {
        const response = await AuthService.register(userData);
        const { access_token, user } = response.data;

        AuthUtils.setToken(access_token);
        commit("registerSuccess");
        commit("userDataSuccess", user);
        await dispatch("subscription/fetchSubscriptionData");

        return response.data;
      } catch (error) {
        commit("registerError", error.message);
        throw error;
      }
    },

    async requestPasswordReset({ commit }, email) {
      commit("passwordResetRequest");

      try {
        const response = await AuthService.requestPasswordReset(email);
        commit("passwordResetSuccess", email);
        return response.data;
      } catch (error) {
        commit("passwordResetError", error.message);
        throw error;
      }
    },

    async resetPassword({ commit }, resetData) {
      commit("passwordResetRequest");

      try {
        const response = await AuthService.resetPassword(resetData);
        commit("passwordResetComplete");
        return response.data;
      } catch (error) {
        commit("passwordResetError", error.message);
        throw error;
      }
    },

    async validateResetToken({ commit }, token) {
      commit("validateTokenRequest");

      try {
        const response = await AuthService.validateResetToken(token);
        commit("validateTokenSuccess", { token, email: response.data.email });
        return response.data;
      } catch (error) {
        commit("validateTokenError", error.message);
        throw error;
      }
    },

    async apiLogout({ commit }) {
      commit("logoutRequest");

      try {
        await AuthService.logout();
        AuthUtils.clearAuthData();
        commit("logoutSuccess");
        AuthUtils.redirectToLogin();
      } catch (error) {
        // Even if API call fails, clear local data and redirect
        AuthUtils.clearAuthData();
        commit("logoutSuccess");
        AuthUtils.redirectToLogin();
      }
    },

    getUserData({ commit }) {
      return new Promise((resolve, reject) => {
        commit("userDataRequest");
        axios
          .get(`${API.BASE_URL}${API.ACCOUNT_CURRENT}`, API.RequestHeaders())
          .then((response) => {
            if (response.status === 200) {
              commit("userDataSuccess", response.data);
              resolve(response.data);
            } else {
              reject(response.data);
            }
          })
          .catch((error) => {
            commit("userDataError");
            console.log(error);
            reject(error.message);
          });
      });
    },

    updateUserData({ commit }, userData) {
      let stringifiedData = qs.stringify({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        middle_name: userData.middle_name,
        birthday: userData.birthday,
        sex: userData.sex,
        address: userData.address,
        country_of_origin: userData.country_of_origin,
        citizenship: userData.citizenship,
        phone: userData.phone,
        passport_number: userData.passport_number,
        facebook: userData.facebook,
        instagram: userData.instagram,
        twitter: userData.twitter,
        pinterest: userData.pinterest,
        snapchat: userData.snapchat,
      });
      return new Promise((resolve, reject) => {
        commit("updateUserRequest");
        axios
          .put(
            `${API.BASE_URL}${API.ACCOUNT}/${userData.id}`,
            stringifiedData,
            API.FormRequestHeaders()
          )
          .then((response) => {
            commit("updateUserSuccess", response.data.original);
            resolve(response.data);
          })
          .catch((error) => {
            console.log(error);
            commit("updateUserError");
            reject(error.message);
          });
      });
    },

    // Initialize authentication state from stored token
    initializeAuth({ commit, dispatch }) {
      const token = AuthUtils.getToken();
      if (token) {
        commit("loginSuccess");
        dispatch("subscription/fetchSubscriptionData");
        // Optionally fetch user data to verify token is still valid
        dispatch("getUserData").catch(() => {
          // Token might be expired, clear it
          AuthUtils.clearAuthData();
          commit("logoutSuccess");
        });
      }
    },

    // Legacy OAuth methods - keeping for backward compatibility during transition
    loginSuccess({ commit, dispatch }, _accessToken) {
      try {
        AuthUtils.setToken(_accessToken);
        commit("loginSuccess");
        dispatch("subscription/fetchSubscriptionData");

        dispatch("getUserData")
          .then(() => {
            console.log("[Auth] User data retrieved successfully");
            // Don't redirect here - let the calling component handle it
          })
          .catch((error) => {
            console.error("[Auth] Failed to get user data with token:", error);
            commit("loginError", "Token validation failed");
          });
      } catch (error) {
        console.error("[Auth] Error storing token:", error);
        commit("loginError", "Error storing authentication data");
      }
    },

    loginError({ commit }) {
      commit("loginError");
    },

    registerSuccess({ commit }, _accessToken) {
      commit("registerSuccess");
      AuthUtils.setToken(_accessToken);
    },

    registerError({ commit }) {
      commit("registerError");
    },

    // Legacy logout - keeping for backward compatibility
    logout({ commit }) {
      commit("logoutRequest");
      return new Promise((resolve, reject) => {
        axios
          .get(`${API.BASE_URL}/logout`, API.RequestHeaders())
          .then((response) => {
            AuthUtils.clearAuthData();
            commit("logoutSuccess", response.data);
            AuthUtils.redirectToLogin();
            resolve(response);
          })
          .catch((error) => {
            commit("logoutError");
            console.log(error);
            reject(error);
          });
      });
    },
  },
  mutations: {
    userDataRequest(state) {
      state.isRequestingUserData = true;
    },
    userDataSuccess(state, userData) {
      state.isRequestingUserData = false;
      state.user = userData;
    },
    userDataError(state, payload) {
      state.isRequestingUserData = false;
      state.errored = true;
      state.error = payload;
    },
    loginRequest(state) {
      state.isRequesting = true;
      state.errored = false;
      state.error = "";
    },
    loginSuccess(state) {
      state.isRequesting = false;
      state.isAuthenticated = true;
      state.errored = false;
      state.error = "";
    },
    loginError(state, payload) {
      state.isRequesting = false;
      state.isAuthenticated = false;
      state.errored = true;
      state.error = payload;
    },
    registerRequest(state) {
      state.isRequesting = true;
      state.errored = false;
      state.error = "";
    },
    registerSuccess(state) {
      state.isRequesting = false;
      state.isAuthenticated = true;
      state.errored = false;
      state.error = "";
    },
    registerError(state, payload) {
      state.isRequesting = false;
      state.isAuthenticated = false;
      state.errored = true;
      state.error = payload;
    },
    logoutRequest(state) {
      state.isRequesting = true;
    },
    logoutSuccess(state) {
      state.user = {};
      state.isAuthenticated = false;
      state.isRequesting = false;
      state.errored = false;
      state.error = "";
    },
    logoutError(state, payload) {
      state.isRequesting = false;
      state.errored = true;
      state.error = payload;
    },
    updateUserRequest(state) {
      state.isUpdatingUser = true;
    },
    updateUserSuccess(state, payload) {
      state.user = payload;
      state.isUpdatingUser = false;
    },
    updateUserError(state, payload) {
      state.isUpdatingUser = false;
      state.errored = true;
      state.error = payload;
    },
    passwordResetRequest(state) {
      state.isRequesting = true;
      state.errored = false;
      state.error = "";
    },
    passwordResetSuccess(state, email) {
      state.isRequesting = false;
      state.passwordResetEmail = email;
      state.errored = false;
      state.error = "";
    },
    passwordResetError(state, payload) {
      state.isRequesting = false;
      state.errored = true;
      state.error = payload;
    },
    passwordResetComplete(state) {
      state.isRequesting = false;
      state.passwordResetEmail = "";
      state.resetToken = null;
      state.resetTokenValid = false;
      state.errored = false;
      state.error = "";
    },
    validateTokenRequest(state) {
      state.isRequesting = true;
      state.errored = false;
      state.error = "";
    },
    validateTokenSuccess(state, { token, email }) {
      state.isRequesting = false;
      state.resetToken = token;
      state.resetTokenValid = true;
      state.passwordResetEmail = email;
      state.errored = false;
      state.error = "";
    },
    validateTokenError(state, payload) {
      state.isRequesting = false;
      state.resetToken = null;
      state.resetTokenValid = false;
      state.errored = true;
      state.error = payload;
    },
  },
  getters: {
    isAuthenticated(state) {
      return state.isAuthenticated;
    },
    getUser(state) {
      return state.user;
    },
    state(state) {
      return state;
    },
    isLoading(state) {
      return state.isRequesting || state.isRequestingUserData;
    },
    hasError(state) {
      return state.errored;
    },
    getError(state) {
      return state.error;
    },
    getPasswordResetEmail(state) {
      return state.passwordResetEmail;
    },
    isResetTokenValid(state) {
      return state.resetTokenValid;
    },
  },
};
